import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  updateDoc, 
  serverTimestamp,
  Timestamp 
} from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { db } from '../firebase';
import { uploadPhoto } from './photoService';
import { ClaudeApiService } from './claudeApiService';
import { AuctionResearchService } from './auctionResearchService';
import { processImageFiles, needsFormatConversion, getFormatName } from '../utils/imageFormatConverter';
import { 
  AIAnalysisResult, 
  AIAnalysisRequest, 
  AnalysisStatus, 
  AnalysisError,
  ClaudeAnalysisResponse 
} from '../types/aiAnalysis';

// Collection reference for AI analysis results
const analysisCollection = collection(db, 'aiAnalyses');

export class AIAnalysisService {
  private claudeService: ClaudeApiService;
  private researchService: AuctionResearchService;

  constructor() {
    this.claudeService = new ClaudeApiService();
    this.researchService = new AuctionResearchService();
  }

  /**
   * Analyze images with Claude AI without uploading to storage first
   * Images are kept in memory until user decides to save the results
   */
  async analyzeImages(request: AIAnalysisRequest, projectId?: string): Promise<AIAnalysisResult> {
    if (request.photos.length === 0) {
      throw new Error('No photos provided for analysis');
    }

    if (request.photos.length > 5) {
      throw new Error('Maximum 5 photos allowed per analysis');
    }

    try {
      // Step 1: Validate images
      this.validateImages(request.photos);

      // Step 2: Convert images to base64 for Claude analysis (in memory)
      console.log('🔄 Preparing images for analysis...');
      const base64Images = await this.convertImagesToBase64(request.photos);

      // Step 3: Analyze with Claude using base64 images
      console.log('🤖 Analyzing images with Claude...');
      const claudeResponse = await this.claudeService.analyzeImages(
        base64Images,
        request.additionalContext
      );

      // Step 4: Research similar items for trustworthiness
      console.log('🔍 Researching similar items for trustworthiness...');
      const { similarExamples, marketResearch } = await this.researchService.researchSimilarItems(
        claudeResponse.brand,
        claudeResponse.model,
        claudeResponse.category,
        claudeResponse.year,
        claudeResponse.description
      );

      // Step 5: Generate confidence factors
      const confidenceFactors = this.researchService.generateConfidenceFactors(
        claudeResponse.brand,
        claudeResponse.model,
        claudeResponse.category,
        claudeResponse.condition,
        request.photos.length,
        similarExamples.length
      );

      // Step 6: Create temporary analysis result (not saved to Firestore yet)
      const analysisResult = this.createTemporaryAnalysisResult(
        request.photos, // Keep original files for later upload if needed
        claudeResponse,
        projectId,
        similarExamples,
        marketResearch,
        confidenceFactors
      );

      console.log('✅ AI Analysis complete (temporary):', analysisResult.id);
      return analysisResult;

    } catch (error) {
      console.error('❌ AI Analysis failed:', error);
      throw this.handleAnalysisError(error);
    }
  }

  /**
   * Save analysis results to Firebase (upload images and store analysis data)
   * Call this when user decides to keep the analysis
   */
  async saveAnalysisResult(analysisResult: AIAnalysisResult): Promise<AIAnalysisResult> {
    try {
      console.log('📤 Saving analysis result to Firebase...');

      // Step 1: Upload photos to Firebase Storage
      const photoUrls = await this.uploadPhotosForAnalysis(
        analysisResult.originalPhotos!, // Original File objects stored temporarily
        analysisResult.projectId
      );

      // Step 2: Create final analysis data for Firestore
      const finalAnalysisData = this.prepareAnalysisDataForFirestore(
        photoUrls,
        analysisResult,
        analysisResult.projectId
      );

      // Step 3: Save to Firestore
      const docRef = await addDoc(analysisCollection, finalAnalysisData);
      
      // Step 4: Return saved result
      const savedResult: AIAnalysisResult = {
        ...analysisResult,
        id: docRef.id,
        photos: photoUrls,
        originalPhotos: undefined, // Clean up temporary data
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      };

      console.log('✅ Analysis result saved:', savedResult.id);
      return savedResult;

    } catch (error) {
      console.error('❌ Failed to save analysis result:', error);
      throw this.handleAnalysisError(error);
    }
  }

  /**
   * Get analysis result by ID
   */
  async getAnalysisResult(analysisId: string): Promise<AIAnalysisResult | null> {
    try {
      const docRef = doc(analysisCollection, analysisId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as AIAnalysisResult;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting analysis result:', error);
      throw error;
    }
  }

  /**
   * Update analysis result (e.g., when user modifies data or adds valuation decision)
   */
  async updateAnalysisResult(
    analysisId: string, 
    updates: Partial<Omit<AIAnalysisResult, 'id' | 'createdAt'>>
  ): Promise<AIAnalysisResult> {
    try {
      const docRef = doc(analysisCollection, analysisId);
      
      // Prepare updates with server timestamp
      const updateData: any = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(docRef, updateData);
      
      // Return updated result
      const updatedDoc = await getDoc(docRef);
      if (!updatedDoc.exists()) {
        throw new Error('Analysis not found after update');
      }
      
      return {
        id: updatedDoc.id,
        ...updatedDoc.data()
      } as AIAnalysisResult;
      
    } catch (error) {
      console.error('Error updating analysis result:', error);
      throw error;
    }
  }

  /**
   * Validate image files before analysis
   */
  private validateImages(photos: File[]): void {
    const supportedFormats = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/webp', 
      'image/heic', 'image/heif', 'image/avif', 'image/gif'
    ];
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    
    for (const photo of photos) {
      // Check file type
      if (!supportedFormats.includes(photo.type.toLowerCase())) {
        throw new Error(`Filformat ${photo.type} stöds inte. Använd: JPG, PNG, HEIC, AVIF, WebP`);
      }
      
      // Check file size
      if (photo.size > maxFileSize) {
        const sizeMB = Math.round(photo.size / (1024 * 1024));
        throw new Error(`Bilden är för stor (${sizeMB}MB). Max storlek: 10MB`);
      }
      
      // Check if it's actually an image
      if (!photo.type.startsWith('image/')) {
        throw new Error('Filen verkar inte vara en bild');
      }
    }
  }

  /**
   * Upload photos to Firebase Storage for the analysis
   */
  private async uploadPhotosForAnalysis(photos: File[], projectId?: string): Promise<string[]> {
    const photoUrls: string[] = [];
    
    for (let i = 0; i < photos.length; i++) {
      const photo = photos[i];
      
      try {
        // Convert File to data URL for uploadPhoto
        const dataUrl = await this.convertFileToDataUrl(photo);
        
        // Upload using existing photo service
        const uploadedPhoto = await uploadPhoto(
          projectId || 'ai-analysis', // Use project ID or fallback
          dataUrl,
          {
            notes: `AI Analysis - Photo ${i + 1}`
          }
        );
        
        photoUrls.push(uploadedPhoto.url);
        
      } catch (error) {
        console.error(`Failed to upload photo ${i + 1}:`, error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        throw new Error(`Kunde inte ladda upp bild ${i + 1}: ${errorMessage}`);
      }
    }
    
    return photoUrls;
  }

  /**
   * Convert File to data URL
   */
  private async convertFileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = () => {
        resolve(reader.result as string);
      };
      
      reader.onerror = () => {
        reject(new Error('Could not read file'));
      };
      
      reader.readAsDataURL(file);
    });
  }

  /**
   * Convert images to base64 for Claude analysis (with format conversion if needed)
   */
  private async convertImagesToBase64(photos: File[]): Promise<string[]> {
    // Step 1: Process images (convert formats if needed)
    const needsConversion = photos.some(photo => needsFormatConversion(photo));
    let processedPhotos = photos;
    
    if (needsConversion) {
      console.log('🔄 Converting modern image formats to JPEG...');
      processedPhotos = await processImageFiles(photos, (current, total, fileName) => {
        if (needsFormatConversion(photos.find(f => f.name === fileName)!)) {
          console.log(`🔄 Converting ${getFormatName(photos.find(f => f.name === fileName)!.type)}: ${fileName}`);
        }
      });
    }
    
    // Step 2: Convert processed images to base64
    const base64Images: string[] = [];
    
    for (let i = 0; i < processedPhotos.length; i++) {
      const photo = processedPhotos[i];
      
      try {
        const base64 = await this.convertFileToDataUrl(photo);
        base64Images.push(base64);
      } catch (error) {
        console.error(`Failed to convert photo ${i + 1} to base64:`, error);
        throw new Error(`Kunde inte läsa bild ${i + 1}`);
      }
    }
    
    return base64Images;
  }

  /**
   * Create temporary analysis result (not saved to Firestore)
   */
  private createTemporaryAnalysisResult(
    originalPhotos: File[],
    claudeResponse: ClaudeAnalysisResponse,
    projectId?: string,
    similarExamples?: any[],
    marketResearch?: any,
    confidenceFactors?: any
  ): AIAnalysisResult {
    // Generate temporary ID
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Create temporary photo URLs for display (object URLs)
    const tempPhotoUrls = originalPhotos.map(photo => URL.createObjectURL(photo));

    const analysisResult: AIAnalysisResult = {
      id: tempId,
      projectId,
      photos: tempPhotoUrls, // Temporary URLs for display
      originalPhotos, // Keep original files for later upload
      analysis: this.cleanAnalysisData({
        brand: claudeResponse.brand,
        model: claudeResponse.model,
        year: claudeResponse.year,
        estimatedValue: claudeResponse.estimatedValueMin && claudeResponse.estimatedValueMax ? {
          min: claudeResponse.estimatedValueMin,
          max: claudeResponse.estimatedValueMax,
          currency: 'SEK' as const,
          confidence: claudeResponse.valueConfidence || 0.5
        } : undefined,
        category: claudeResponse.category,
        condition: claudeResponse.condition,
        description: claudeResponse.description,
        materials: claudeResponse.materials,
        dimensions: claudeResponse.dimensions,
        additionalInfo: claudeResponse.additionalInfo,
        // New trustworthiness fields
        similarExamples,
        marketResearch,
        confidenceFactors
      }),
      confidence: claudeResponse.overallConfidence,
      analyzedAt: Timestamp.now(),
      createdAt: Timestamp.now(), // Temporary timestamp
      updatedAt: Timestamp.now()
    };

    // 🧹 MEMORY LEAK FIX: Store URLs for cleanup
    (analysisResult as any)._tempPhotoUrls = tempPhotoUrls;

    return analysisResult;
  }

  /**
   * Prepare analysis data for Firestore (clean undefined values)
   */
  private prepareAnalysisDataForFirestore(
    photoUrls: string[],
    analysisResult: AIAnalysisResult,
    projectId?: string
  ): any {
    const analysisData: any = {
      photos: photoUrls,
      analysis: analysisResult.analysis, // Already cleaned in createTemporaryAnalysisResult
      confidence: analysisResult.confidence,
      analyzedAt: analysisResult.analyzedAt,
      createdAt: serverTimestamp() as Timestamp,
      updatedAt: serverTimestamp() as Timestamp
    };

    // Only add projectId if it's provided (avoid undefined values in Firestore)
    if (projectId) {
      analysisData.projectId = projectId;
    }

    return analysisData;
  }

  /**
   * Helper function to remove undefined values from analysis data
   */
  private cleanAnalysisData(analysisData: any): any {
    const cleaned: any = {};
    Object.keys(analysisData).forEach(key => {
      if (analysisData[key] !== undefined) {
        if (typeof analysisData[key] === 'object' && analysisData[key] !== null && !Array.isArray(analysisData[key])) {
          const nestedCleaned = this.cleanAnalysisData(analysisData[key]);
          if (Object.keys(nestedCleaned).length > 0) {
            cleaned[key] = nestedCleaned;
          }
        } else if (Array.isArray(analysisData[key]) && analysisData[key].length > 0) {
          cleaned[key] = analysisData[key];
        } else if (!Array.isArray(analysisData[key])) {
          cleaned[key] = analysisData[key];
        }
      }
    });
    return cleaned;
  }

  /**
   * Handle and standardize analysis errors
   */
  private handleAnalysisError(error: any): AnalysisError {
    if (error instanceof Error) {
      // Network/API errors
      if (error.message.includes('fetch') || error.message.includes('network')) {
        return {
          type: 'network',
          message: 'Nätverksfel. Kontrollera din internetanslutning och försök igen.',
          code: 'NETWORK_ERROR'
        };
      }
      
      // Claude API errors
      if (error.message.includes('Claude API') || error.message.includes('API key')) {
        return {
          type: 'api',
          message: 'Problem med AI-tjänsten. Försök igen om en stund.',
          code: 'API_ERROR'
        };
      }
      
      // Quota/rate limit errors
      if (error.message.includes('rate limit') || error.message.includes('quota')) {
        return {
          type: 'quota',
          message: 'API-gränsen nådd. Försök igen om en stund.',
          code: 'QUOTA_EXCEEDED'
        };
      }
      
      // Validation errors
      if (error.message.includes('format') || error.message.includes('stor')) {
        return {
          type: 'validation',
          message: error.message,
          code: 'VALIDATION_ERROR'
        };
      }
      
      // Generic error
      return {
        type: 'api',
        message: error.message || 'Ett oväntat fel inträffade.',
        code: 'UNKNOWN_ERROR'
      };
    }
    
    // Fallback for non-Error objects
    return {
      type: 'api',
      message: 'Ett oväntat fel inträffade.',
      code: 'UNKNOWN_ERROR'
    };
  }

  /**
   * Check if service is properly configured
   */
  isConfigured(): boolean {
    return this.claudeService.isConfigured();
  }

  /**
   * Get estimated cost for analysis (informational)
   */
  getEstimatedCost(imageCount: number): { sek: number; description: string } {
    const baseCost = 1.5; // ~$0.15 per image in SEK
    const totalCost = imageCount * baseCost;
    
    return {
      sek: Math.round(totalCost * 100) / 100,
      description: `Uppskattad kostnad för ${imageCount} ${imageCount === 1 ? 'bild' : 'bilder'}`
    };
  }

  /**
   * Clean up temporary object URLs to prevent memory leaks
   */
  cleanupTemporaryUrls(analysisResult: AIAnalysisResult): void {
    if ((analysisResult as any)._tempPhotoUrls) {
      const tempPhotoUrls = (analysisResult as any)._tempPhotoUrls as string[];
      tempPhotoUrls.forEach(url => {
        URL.revokeObjectURL(url);
      });
      delete (analysisResult as any)._tempPhotoUrls;
    }
  }
} 